import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { openBillingPortal, syncMySubscription } from "@/lib/billing.functions";
import { reservePaymentWindow, goToPayment, closePaymentWindow } from "@/lib/open-payment";
import { Check, Crown, ArrowRight } from "lucide-react";
import { myPlanQuery } from "@/lib/queries";
import {
  PLANS,
  PLAN_ORDER,
  PLAN_RANK,
  FAIR_USE_NOTE,
  PLAN_DISCLAIMER,
  UNLIMITED_LIMITS,
  remainingThisMonth,
  type PlanId,
  type AdUsage,
} from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { HelpLink } from "@/components/help/HelpLink";
import { getMyQuotas } from "@/lib/plans.functions";
import { periodLabel, quotaText, renewLabel } from "@/lib/quotas";

export const Route = createFileRoute("/_authenticated/min-niva")({
  head: () => ({
    meta: [
      { title: "Din nivå — Sellavo" },
      {
        name: "description",
        content: "Allt som ingår i din nivå på Sellavo: annonser, bilder, lyft och gränser.",
      },
      { property: "og:title", content: "Din nivå — Sellavo" },
      { property: "og:description", content: "Se vad som ingår i din nivå på Sellavo." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { betalning?: string } =>
    typeof search["betalning"] === "string" ? { betalning: search["betalning"] } : {},
  loader: ({ context }) => context.queryClient.ensureQueryData(myPlanQuery()),
  component: MyPlanPage,
});

function MyPlanPage() {
  const { betalning } = useSearch({ from: "/_authenticated/min-niva" });
  const queryClient = useQueryClient();
  const sync = useServerFn(syncMySubscription);
  const portal = useServerFn(openBillingPortal);
  const [portalLoading, setPortalLoading] = useState(false);
  const synced = useRef(false);

  // Efter en betalning hämtar vi abonnemanget från Stripe och uppdaterar nivån.
  useEffect(() => {
    if (synced.current) return;
    synced.current = true;
    void sync({})
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ["my-plan"] });
        void queryClient.invalidateQueries({ queryKey: ["my-quotas"] });
        if (betalning === "klar") toast.success("Tack! Din nivå är uppdaterad.");
      })
      .catch(() => {});
  }, [betalning, queryClient, sync]);

  const openPortal = async () => {
    // Kundportalen måste lämna den inbäddade preview-ramen. Fliken öppnas
    // före serveranropet så att webbläsarens popupskydd godkänner den.
    const portalWindow = reservePaymentWindow();
    setPortalLoading(true);
    try {
      const { url } = await portal({ data: { returnOrigin: window.location.origin } });
      const opened = goToPayment(url, portalWindow);
      if (!opened) {
        toast("Din webbläsare blockerade betalningsfönstret.", {
          duration: 60000,
          action: { label: "Öppna betalningen", onClick: () => window.open(url, "_blank") },
        });
      }
    } catch (err) {
      closePaymentWindow(portalWindow);
      toast.error(err instanceof Error ? err.message : "Kunde inte öppna betalningssidan.");
    } finally {
      setPortalLoading(false);
    }
  };

  const { data: planStatus } = useQuery(myPlanQuery());
  const { data: quotas } = useQuery({ queryKey: ["my-quotas"], queryFn: () => getMyQuotas() });

  const currentPlan: PlanId = (planStatus?.plan as PlanId) ?? "free";
  const plan = PLANS[currentPlan];
  const unlimited = planStatus?.unlimited === true;
  const limits = unlimited ? UNLIMITED_LIMITS : plan.limits;
  const usage: AdUsage = planStatus?.usage ?? { day: 0, week: 0, month: 0 };
  const left = unlimited ? null : remainingThisMonth(currentPlan, usage);
  const hasFairUse = plan.features.some((f) => f.fairUse);

  const next = PLAN_ORDER.find((id) => PLAN_RANK[id] > PLAN_RANK[currentPlan]);
  const nextPlan = next ? PLANS[next] : null;
  const extras = nextPlan
    ? nextPlan.features.filter((f) => !f.text.startsWith("Allt i")).slice(0, 4)
    : [];

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground sm:text-4xl">
        Din nivå
      </h1>
      <p className="mt-1 text-sm text-muted-foreground sm:mt-2 sm:text-base">
        Här ser du precis vad som ingår i ditt konto just nu.
      </p>

      <section className="mt-5 rounded-xl border border-border bg-card p-4 shadow-sm sm:mt-8 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-heading text-xl font-bold text-foreground">
            <Crown className="h-5 w-5 text-primary" /> {plan.name}
          </p>
          <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
            {unlimited ? "Utan gränser" : plan.priceLabel}
          </span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{plan.tagline}</p>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Fact
            label="Annonser kvar i månaden"
            value={left === null ? "Obegränsat" : String(left)}
          />
          <Fact
            label="Bilder per annons"
            value={String(limits.maxImages)}
          />
          <Fact
            label="Lyft per månad"
            value={
              limits.promotionsPerMonth > 1000
                ? "Obegränsat"
                : limits.promotionsPerMonth === 0
                  ? "Ingår inte"
                  : String(limits.promotionsPerMonth)
            }
          />
          <Fact label="Video på annonsen" value={limits.video ? "Ingår" : "Ingår inte"} />
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Button asChild variant="outline">
            <Link to="/priser">Se alla nivåer</Link>
          </Button>
          <Button variant="secondary" onClick={openPortal} disabled={portalLoading}>
            {portalLoading ? "Öppnar…" : "Hantera betalning och kvitton"}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Där byter du kort, ser kvitton eller säger upp. Uppsägning gäller från nästa period.
        </p>
      </section>

      {quotas && (
        <section className="mt-5 rounded-xl border border-border bg-card p-4 shadow-sm sm:mt-8 sm:p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-heading text-lg font-semibold text-foreground">
              Kvar den här perioden
            </h2>
            <p className="text-sm text-muted-foreground">
              {periodLabel(quotas.periodStart, quotas.periodEnd) ?? "Senaste 30 dagarna"}
              {renewLabel(quotas.periodEnd) ? ` · ${renewLabel(quotas.periodEnd)}` : ""}
            </p>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Perioden räknas från den dag du startade abonnemanget, inte från månadsskiftet.
          </p>
          <ul className="mt-3 divide-y divide-border">
            {quotas.rows.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground">{row.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {row.window === "day" ? "Nollställs varje dygn" : "Nollställs vid förnyelse"}
                  </span>
                </span>
                <span
                  className={
                    !row.included
                      ? "shrink-0 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"
                      : "shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary"
                  }
                >
                  {quotaText(row)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-5 rounded-xl border border-border bg-card p-4 shadow-sm sm:mt-8 sm:p-6">
        <h2 className="font-heading text-lg font-semibold text-foreground">Det här ingår</h2>
        <ul className="mt-3 space-y-2">
          {plan.features.map((f) => (
            <li key={f.text} className="flex items-start gap-2 text-sm text-foreground">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                {f.text}
                {f.fairUse && <span className="text-muted-foreground">*</span>}
              </span>
            </li>
          ))}
        </ul>

        {plan.notes.length > 0 && (
          <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/40 p-3">
            <p className="text-sm font-semibold text-foreground">Dina gränser</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {plan.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </div>
        )}

        {hasFairUse && <p className="mt-3 text-xs text-muted-foreground">* {FAIR_USE_NOTE}</p>}
        <p className="mt-2 text-xs text-muted-foreground">{PLAN_DISCLAIMER}</p>
      </section>

      {nextPlan && (
        <section className="mt-5 rounded-xl border border-border bg-card p-4 shadow-sm sm:mt-8 sm:p-6">
          <h2 className="font-heading text-lg font-semibold text-foreground">
            Med {nextPlan.name} får du dessutom
          </h2>
          <ul className="mt-3 space-y-2">
            {extras.map((f) => (
              <li key={f.text} className="flex items-start gap-2 text-sm text-foreground">
                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{f.text}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button asChild>
              <Link to="/priser">Jämför nivåer</Link>
            </Button>
            <Link to="/konto" className="text-sm font-semibold text-foreground hover:underline">
              Mitt konto
            </Link>
          </div>
        </section>
      )}

      {!nextPlan && (
        <div className="mt-5 flex flex-wrap items-center gap-3 sm:mt-8">
          <Button asChild variant="outline">
            <Link to="/konto">Mitt konto</Link>
          </Button>
        </div>
      )}
      <div className="mt-8 flex justify-center">
        <HelpLink topic="niva" />
      </div>
    </main>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-heading text-base font-bold text-foreground">{value}</p>
    </div>
  );
}
